/* eslint-disable default-case */
import { School, Scores, POI } from "@local-logic/client";
import { baseStyles } from "@local-logic/core/ui";
import { scoreCategories } from "~/sdks/LocalContent/constants/score-categories";
import { FeatureCollection } from "@turf/turf";
import React, {
  useContext,
  useState,
  useMemo,
  useReducer,
  SetStateAction,
} from "react";
import { ValidationError } from "yup";
import errorTracker from "~/utils/errorTracker";
import useEffectOnce from "~/hooks/useEffectOnce";
import { useStateManagerContext } from "~/context/stateManager";
import useDebounce from "~/hooks/useDebounce";
import { useTracking } from "@local-logic/core/tracking";
import { validateOptions } from "./schema";
import {
  type LocalContentProps,
  type ScoreCategories,
  AnalyticsEvents,
  TrackingPipelineEvents,
} from "./types";

interface Location {
  label?: string;
  lat: number;
  lng: number;
}

export type DefaultContext = {
  country: string | "";
  setCountry: (country: string | "") => void;
  activeScore: keyof Scores["location"] | "";
  setActiveScore: (score: keyof Scores["location"] | "") => void;
  activeCategory: ScoreCategories;
  setActiveCategory: (value: ScoreCategories) => void;
  activePOI: POI | School | undefined;
  setActivePOI: (poi: POI | School | undefined) => void;
  hoveredPOI: POI | School | undefined;
  setHoveredPOI: (poi: POI | School | undefined) => void;
  commuteGeojson: FeatureCollection | google.maps.LatLng[] | undefined;
  setCommuteGeojson: (
    geojson: FeatureCollection | google.maps.LatLng[] | undefined
  ) => void;
  destination: Location | undefined;
  setDestination: (data: Location | undefined) => void;
  showCommuteCalculator?: boolean;
  setShowCommuteCalculator: React.Dispatch<SetStateAction<boolean>>;
  isInputAutoFocused?: boolean;
  setIsInputAutoFocused: React.Dispatch<SetStateAction<boolean>>;
  location: {
    lat?: number;
    lng?: number;
  };
  hideScores: boolean;
  collapseScores: boolean;
};

const LocalContentContext = React.createContext<DefaultContext>(
  {} as DefaultContext
);
const useLocalContent = () => useContext(LocalContentContext);

interface ScoreCategory {
  activeCategory: ScoreCategories;
  activeScoreByCategory: {
    [key in ScoreCategories]: keyof Scores["location"] | "";
  };
}

const scoreCategoryReducer = (
  state: ScoreCategory,
  action:
    | { type: "CATEGORY"; value: ScoreCategories }
    | { type: "SCORE"; value: keyof Scores["location"] | "" }
  // eslint-disable-next-line consistent-return
) => {
  switch (action.type) {
    case "CATEGORY": {
      return {
        ...state,
        activeCategory: action.value,
      };
    }

    case "SCORE": {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const newActiveCategory = scoreCategories.find((c) => {
        if (action.value === "") {
          return c.value === state.activeCategory;
        }

        return c.scores.includes(action.value);
      })!.value;

      return {
        activeCategory: newActiveCategory,
        activeScoreByCategory: {
          ...state.activeScoreByCategory,
          [newActiveCategory]: action.value,
        },
      };
    }
  }
};

const LocalContentProvider = ({ children }: { children: React.ReactNode }) => {
  const { sdkState } = useStateManagerContext<LocalContentProps>();
  const tracking = useTracking();
  const debouncedOptions = useDebounce(sdkState.options!, 250);

  const scoreCategoryDefault: ScoreCategory =
    window.innerWidth < baseStyles.screenSizes.tablet
      ? {
          activeCategory: "commute",
          activeScoreByCategory: {
            commute: "transit_friendly",
            amenities: "groceries",
            character: "quiet",
            nature: "parks",
            education: "primary_schools",
            wellness: "wellness",
          },
        }
      : {
          activeCategory: "commute",
          activeScoreByCategory: {
            commute: "",
            amenities: "",
            character: "",
            nature: "",
            education: "",
            wellness: "",
          },
        };

  const [scoreCategory, setScoreCategory] = useReducer(
    scoreCategoryReducer,
    scoreCategoryDefault
  );

  const [activePOI, setActivePOI] = useState<POI | School>();
  const [hoveredPOI, setHoveredPOI] = useState<POI | School>();
  const [commuteGeojson, setCommuteGeojson] = useState<
    FeatureCollection | google.maps.LatLng[]
  >();
  const [destination, setDestination] = useState<Location>();
  const [showCommuteCalculator, setShowCommuteCalculator] =
    useState<boolean>(false);
  const [isInputAutoFocused, setIsInputAutoFocused] = useState<boolean>(false);
  const [country, setCountry] = useState<string>("CAN,USA");
  const [validated, setValidated] = useState<boolean>(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  const location = useMemo(() => {
    const hasMarker = !!debouncedOptions?.marker;

    if (hasMarker) {
      return {
        lat: debouncedOptions?.marker?.lat,
        lng: debouncedOptions?.marker?.lng,
      };
    }

    return {
      lat: debouncedOptions?.lat,
      lng: debouncedOptions?.lng,
    };
  }, [debouncedOptions]);

  const hideScores = useMemo(
    () => (sdkState.options?.hideScores ? sdkState.options?.hideScores : false),
    [sdkState.options]
  );

  const collapseScores = useMemo(
    () =>
      sdkState.options?.collapseScores
        ? sdkState.options?.collapseScores
        : false,
    [sdkState.options]
  );

  useEffectOnce(() => {
    const validate = async () => {
      try {
        errorTracker.addBreadCrumb(
          "LocalContentProvider",
          "Validating state options",
          {
            options: sdkState.options!,
          }
        );

        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        await validateOptions(sdkState.options!);
        setValidated(true);
      } catch (error) {
        const validationError = error as ValidationError;
        setValidationErrors(validationError.errors);

        errorTracker.addBreadCrumb(
          "LocalContentProvider",
          "State option validation failed",
          {
            errors: validationError.errors,
          }
        );
      }
    };

    if (!validated) {
      validate();
    }

    tracking.postEvent({
      eventType: TrackingPipelineEvents.SDK_VIEWED,
    });

    tracking.trackAnalyticsEvent({
      eventName: AnalyticsEvents.SDK_VIEWED,
    });
  });

  const { activeCategory } = scoreCategory;
  const activeScore = scoreCategory.activeScoreByCategory[activeCategory];

  const setActiveScore = (value: keyof Scores["location"] | "") => {
    setScoreCategory({ type: "SCORE", value });
    errorTracker.addBreadCrumb(
      "LocalContentProvider",
      `Active score changed to: ${String(value)}`
    );
  };

  const setActiveCategory = (value: ScoreCategories) => {
    setScoreCategory({ type: "CATEGORY", value });
    errorTracker.addBreadCrumb(
      "LocalContentProvider",
      `Active category changed to: ${value}`
    );
  };

  const contextValue = useMemo(
    () => ({
      country,
      setCountry,
      activeScore,
      setActiveScore,
      activeCategory,
      setActiveCategory,
      activePOI,
      setActivePOI,
      hoveredPOI,
      setHoveredPOI,
      commuteGeojson,
      setCommuteGeojson,
      destination,
      setDestination,
      showCommuteCalculator,
      isInputAutoFocused,
      setIsInputAutoFocused,
      setShowCommuteCalculator,
      location,
      hideScores,
      collapseScores,
    }),
    [
      country,
      setCountry,
      activeScore,
      setActiveScore,
      activeCategory,
      setActiveCategory,
      activePOI,
      setActivePOI,
      hoveredPOI,
      setHoveredPOI,
      commuteGeojson,
      setCommuteGeojson,
      destination,
      setDestination,
      showCommuteCalculator,
      isInputAutoFocused,
      setIsInputAutoFocused,
      setShowCommuteCalculator,
      location,
      hideScores,
      collapseScores,
    ]
  );

  if (!validated) {
    if (validationErrors.length) {
      const error = "Could not load Local Content";
      // eslint-disable-next-line no-console
      console.error(`${error}: ${validationErrors.join(", ")}`);
      return <p>{error}</p>;
    }

    return <></>;
  }

  return (
    <LocalContentContext.Provider value={contextValue}>
      {children}
    </LocalContentContext.Provider>
  );
};

export { useLocalContent, LocalContentProvider, type Location };
